import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../utils/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (!urlToken) {
      toast.error('Invalid reset link');
      navigate('/login');
      return;
    }
    setToken(urlToken);
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen noise-bg">
      <Navbar />
      
      <div className="pt-32 pb-12">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-[#0A0A0A] border border-border rounded-lg p-8">
            {!success ? (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <Lock className="w-8 h-8 text-[#00E055]" />
                  <h1 className="text-3xl font-black tracking-tighter uppercase" style={{fontFamily: 'Unbounded, sans-serif'}}>
                    RESET <span className="text-[#00E055]">PASSWORD</span>
                  </h1>
                </div>

                <p className="text-muted-foreground mb-6">
                  Enter your new password below.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">New Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="Enter new password"
                      className="bg-muted border-border"
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="Confirm new password"
                      className="bg-muted border-border"
                      disabled={loading}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#00E055] hover:bg-[#00E055]/90 text-black font-bold"
                    disabled={loading}
                  >
                    {loading ? 'RESETTING...' : 'RESET PASSWORD'}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center py-6">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-3 text-green-400" style={{fontFamily: 'Unbounded, sans-serif'}}>
                  PASSWORD RESET!
                </h2>
                <p className="text-muted-foreground mb-6">
                  Your password has been reset successfully. You can now log in with your new password.
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-[#00E055] hover:bg-[#00E055]/90 text-black font-bold w-full"
                >
                  GO TO LOGIN
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
